import { cloneDeep } from "lodash-es"

import type {
  TrackingPageThemeImageApiValue,
  TrackingPageThemeImageBannerBlock,
  TrackingPageThemeImageField,
  TrackingPageThemeLayoutInlinePayload,
  TrackingPageThemeMediaImage,
} from "@/models/ITrackingPageThemeLayoutConfig"
import {
  emptyTrackingPageThemeMediaImage,
  TrackingPageThemeContentColumnType,
} from "@/models/ITrackingPageThemeLayoutConfig"

export type { TrackingPageThemeImageApiValue } from "@/models/ITrackingPageThemeLayoutConfig"

const isThemeImageEmpty = (row: TrackingPageThemeMediaImage): boolean =>
  !row.gid?.trim() && !row.url?.trim()

/**
 * 给从接口进来的图片元素补一个唯一稳定 id：旧数据没有 id 时，过去回退到 gid，
 * 导致多列/多块使用同一张图片时 id 冲突（勾一个全勾、改一个全改、保存丢列）。
 * 在入库这一处统一补齐唯一 id，draft 派生只需读取它，身份既唯一又能穿过拖拽往返。
 * 仅在缺失时生成，已有 id（含 draft 往返写回的）保持不变。
 */
const ensureStableElementId = (existing: string | undefined): string => {
  const trimmed = existing?.trim()

  if (trimmed) {
    return trimmed
  }
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID()
  }

  return `tl-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

export const normalizeThemeImageFromApi = (
  value: TrackingPageThemeImageApiValue | undefined,
): TrackingPageThemeImageField => {
  if (value == null) {
    return null
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return null
    }

    const first = value[0]

    // gid/url 均为空但含 alt 时视为 "alt-only stub"，保留而非丢弃
    return isThemeImageEmpty(first) ? (first.alt?.trim() ? first : null) : first
  }

  return isThemeImageEmpty(value) ? (value.alt?.trim() ? value : null) : value
}

/** 保存接口：无图时为 `{ gid: "", url: "", alt: "" }`，不能为 null 或 [] */
export const toThemeImageSaveValue = (
  value: TrackingPageThemeImageApiValue | undefined,
): TrackingPageThemeMediaImage => {
  const normalized = normalizeThemeImageFromApi(value)

  if (!normalized) {
    return emptyTrackingPageThemeMediaImage()
  }

  return normalized
}

const normalizeImageBannerBlockFromApi = (block: TrackingPageThemeImageBannerBlock): void => {
  block.image = normalizeThemeImageFromApi(block.image as TrackingPageThemeImageApiValue)
}

const setImageBannerBlockForSave = (block: TrackingPageThemeImageBannerBlock): void => {
  block.image = toThemeImageSaveValue(block.image as TrackingPageThemeImageApiValue)
}

export const normalizeThemeLayoutPayloadImagesFromApi = (
  payload: TrackingPageThemeLayoutInlinePayload,
): TrackingPageThemeLayoutInlinePayload => {
  const next = cloneDeep(payload)

  if ("columns" in next.content) {
    for (const column of next.content.columns) {
      if (column.type === TrackingPageThemeContentColumnType.Image) {
        column.image = normalizeThemeImageFromApi(column.image as TrackingPageThemeImageApiValue)
        column.id = ensureStableElementId(column.id)
      }
    }
  } else {
    normalizeImageBannerBlockFromApi(next.content.top_section.image_banner)
  }

  for (const item of next.content.bottom_section.images) {
    item.image = normalizeThemeImageFromApi(item.image as TrackingPageThemeImageApiValue)
    item.id = ensureStableElementId(item.id)
  }

  return next
}

export const prepareThemeLayoutPayloadForSave = (
  payload: TrackingPageThemeLayoutInlinePayload,
): TrackingPageThemeLayoutInlinePayload => {
  const next = cloneDeep(payload)

  if ("columns" in next.content) {
    for (const column of next.content.columns) {
      if (column.type === TrackingPageThemeContentColumnType.Image) {
        column.image = toThemeImageSaveValue(column.image as TrackingPageThemeImageApiValue)
        // id 仅用于前端拖拽排序/逐列编辑的稳定身份，后端不接受该字段，发送前剥离。
        delete column.id
      }
    }
  } else {
    setImageBannerBlockForSave(next.content.top_section.image_banner)
  }

  for (const item of next.content.bottom_section.images) {
    item.image = toThemeImageSaveValue(item.image as TrackingPageThemeImageApiValue)
    // id 仅用于前端拖拽排序的稳定身份，后端不接受该字段，发送前剥离。
    delete item.id
  }

  return next
}
